<template>
  <div class="col-span-5 pt-3 pr-3 overflow-visible"
       :class="`border-solid border-0 border-l-2 border-${message.borderColor}`">
    <div class="pt-4 overflow-visible"
         :class="isCarousel(message.data.content) ? 'pl-4' : 'grid grid-cols-5'"
    >
      <div class="relative overflow-visible"
           :class="{ 'col-span-3 col-start-3': !isCarousel(message.data.content) }"
      >
        <vx-tooltip :text="tooltipText"
                    :key="tooltipText"
                    position="left"
                    class="justify-end"
                    :enabled="!isBadgeHovered"
                    delay="0.2s">
          <carousel-preview v-if="isCarousel(message.data.content)"
                            :payload="message.data.content.payload"
                            :layout="message.data.metadata && message.data.metadata.product_layout"
          />
          <bubble v-else
                  :content="message.data.content"
                  :original-content="message.data.originalContent"
                  :bubble-classes="isEmailBot ? 'bg-white border border-gray-bubble' : 'border-none'"
                  :text-classes="isEmailBot ? 'text-body' : 'text-white whitespace-pre-wrap'"
                  :render-html="isEmailBot"
                  :quick-reply-classes="quickReplyClasses"
                  @context-tooltip-show="handleContextTooltipShow"
                  @context-tooltip-hide="handleContextTooltipHide"
          />
        </vx-tooltip>
        <rendering-failure-badge-tooltip v-if="hasButtonsNotRendered"
                                         :title="buttonsNotRenderedTooltipTitle"
                                         :text="buttonsNotRenderedTooltip"
                                         @badge-enter="isBadgeHovered = true"
                                         @badge-leave="isBadgeHovered = false"
        />
        <div v-if="isPositiveFeedback || isNegativeFeedback" class="flex justify-end mt-2">
          <vx-tooltip :title="feedbackTooltip.title"
            :text="feedbackTooltip.text"
            position="top"
            delay="0.2s"
          >
            <icon-thumbs-up v-if="isPositiveFeedback" class="text-success" />
            <icon-thumbs-down v-else class="text-focus-error" />
          </vx-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Bubble from '../../../preview/Bubble.vue'
import CarouselPreview from '@/components/preview/CarouselPreview.vue'
import RenderingFailureBadgeTooltip from '@/components/analytics/conversations/detail/RenderingFailureBadgeTooltip.vue'
import IconThumbsUp from '@/assets/images/icons/chat-widget/IconThumbsUp.vue'
import IconThumbsDown from '@/assets/images/icons/chat-widget/IconThumbsDown.vue'
import datetimeUtils from '@/utils/datetime.utils'
import { OUTCOME_TYPES } from '@/utils/bot-json.constants'

export default {
  name: 'ConversationBotMessage',

  components: {
    Bubble,
    CarouselPreview,
    RenderingFailureBadgeTooltip,
    IconThumbsUp,
    IconThumbsDown
  },

  data() {
    return {
      contextKey: null,
      isBadgeHovered: false
    }
  },

  computed: {
    buttonsNotRendered() {
      return this.message?.data?.metadata?.rendering_fallback?.buttons_not_rendered || []
    },

    hasButtonsNotRendered() {
      return this.buttonsNotRendered.length > 0
    },

    buttonsNotRenderedTooltipTitle() {
      return this.buttonsNotRendered.length === 1 ?
        'Button not rendered' :
        'Buttons not rendered'
    },

    buttonsNotRenderedTooltip() {
      return this.buttonsNotRendered.map(button => {
        const title = this.escapeHtml(button.title)
        const payload = this.escapeHtml(button.original_payload)
        const reason = this.escapeHtml(button.reason)
        const resolvedValue = this.escapeHtml(button.resolved_value)

        return `<b>${title}</b><br/>
                Original payload: ${payload}<br/>
                Resolved value: ${resolvedValue}<br/>
                Reason: ${reason}`
      }).join('<br/><br/>')
    },

    isPositiveFeedback() {
      return this.message.feedback === OUTCOME_TYPES.CUSTOMER_ANSWERED_YES
    },

    isNegativeFeedback() {
      return this.message.feedback === OUTCOME_TYPES.CUSTOMER_ANSWERED_NO
    },

    feedbackTooltip() {
      return this.isPositiveFeedback ? {
        title: 'Positive feedback',
        text: 'The customer voted positively for the answer.'
      } : {
        title: 'Negative feedback',
        text: 'The customer voted negatively for the answer.'
      }
    },

    tooltipText() {
      const timestamp = this.formatTimestamp(this.message.sent || this.message.timestamp)
      if (this.contextKey) {
        return `${timestamp}
                <div style="font-size: 0.875rem; white-space: nowrap; margin-top: 10px">
                  <b>Interpolation key:</b>
                </div>
                ${this.contextKey}
               `
      }
      return timestamp
    }
  },

  props: {
    message: {
      type: Object,
      required: true
    },
    isEmailBot: {
      type: Boolean,
      default: false
    },
    selectedQuickReplyIndex: {
      type: Number
    }
  },

  methods: {
    isCarousel(content) {
      if (!content) return false
      return content.type === 'structure' && Array.isArray(content.payload)
    },

    formatTimestamp(timestamp) {
      return datetimeUtils.toMoment(timestamp).format('LL HH:mm:ss')
    },

    quickReplyClasses(index) {
      if (index === this.selectedQuickReplyIndex) {
        return 'border-solid border-focus text-focus shadow-focus'
      }

      return 'border-solid border-primary text-primary'
    },

    handleContextTooltipShow(contextKey) {
      this.contextKey = contextKey
    },

    handleContextTooltipHide() {
      this.contextKey = null
    },

    escapeHtml(value) {
      if (value == null) return ''

      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    }
  }
}
</script>
<style scoped>
::v-deep a {
  @apply text-white underline;
}
</style>
